import { CssVariables } from '@activebrands/core-web/types/css-types';
import defaultTheme from './default';

export const darkColorCssVariables: CssVariables<string> = {
    // UI
    '--color-ui-content-primary': 'var(--color-base-white)',
    '--color-ui-content-secondary': 'var(--color-base-beige-400)',
    '--color-ui-content-hover': 'var(--color-base-beige-300)',
    '--color-ui-content-disabled': 'var(--color-base-beige-700)',
    '--color-ui-content-sale': 'var(--color-base-red-light)',
    '--color-ui-bg-background': 'var(--color-base-black)',
    '--color-ui-bg-surface': 'var(--color-base-beige-900)',
    '--color-ui-bg-block': 'var(--color-base-beige-800)',
    '--color-ui-border-default': 'var(--color-base-white-25)',

    // Gradients and overlay
    '--color-gradient-default-hard': 'var(--color-base-black)',
    '--color-gradient-default-soft': 'var(--color-base-black-25)',
    '--color-gradient-default-transparent': 'var(--color-base-black-0)',
    '--color-gradient-surface-hard': 'var(--color-base-beige-800)',
    '--color-gradient-surface-soft': 'var(--color-base-brown-50)',
    '--color-gradient-surface-transparent': 'var(--color-base-brown-0)',
    '--color-overlay-default': 'var(--color-base-white-25)',

    // Button tokens
    '--color-btn-primary-fill-default': 'var(--color-base-beige-300)',
    '--color-btn-primary-fill-hover': 'var(--color-base-beige-200)',
    '--color-btn-primary-fill-active': 'var(--color-base-beige-800)',
    '--color-btn-primary-fill-disabled': 'var(--color-base-beige-200)',
    '--color-btn-primary-content-default': 'var(--color-base-black)',
    '--color-btn-primary-content-hover': 'var(--color-base-black)',
    '--color-btn-primary-content-active': 'var(--color-base-white)',
    '--color-btn-primary-content-disabled': 'var(--color-base-beige-700)',
    '--color-btn-secondary-fill-default': 'var(--color-base-beige-800)',
    '--color-btn-secondary-fill-hover': 'var(--color-base-beige-700)',
    '--color-btn-secondary-fill-active': 'var(--color-base-beige-400)',
    '--color-btn-secondary-fill-disabled': 'var(--color-base-beige-700)',
    '--color-btn-secondary-content-default': 'var(--color-base-white)',
    '--color-btn-secondary-content-hover': 'var(--color-base-white)',
    '--color-btn-secondary-content-active': 'var(--color-base-black)',
    '--color-btn-secondary-content-disabled': 'var(--color-base-beige-300)',

    // Inputs
    '--color-input-default-fill-default': 'var(--color-base-black)',
    '--color-input-default-fill-hover': 'var(--color-base-beige-900)',
    '--color-input-default-fill-active': 'var(--color-base-black)',
    '--color-input-default-fill-disabled': 'var(--color-base-beige-800)',
    '--color-input-default-label-default': 'var(--color-base-white-50)',
    '--color-input-default-content-default': 'var(--color-base-white-50)',
    '--color-input-default-content-hover': 'var(--color-base-white-75)',
    '--color-input-default-content-active': 'var(--color-base-white)',
    '--color-input-default-content-disabled': 'var(--color-base-white-50)',
    '--color-input-default-content-error': 'var(--color-base-red-light)',
    '--color-input-default-border-default': 'var(--color-base-white-25)',
    '--color-input-default-border-hover': 'var(--color-base-white-50)',
    '--color-input-default-border-active': 'var(--color-base-white)',
    '--color-input-default-border-error': 'var(--color-base-red-light)',

    // Badges and banners
    '--color-badge-sale-new': 'var(--color-base-white)',
    '--color-badge-sale': 'var(--color-base-brand-pink)',
    '--color-badge-article-bg': 'var(--color-base-black)',
    '--color-badge-article-label': 'var(--color-base-beige-300)',
    '--color-banner-pri-fill': 'var(--color-base-beige-300)',
    '--color-banner-pri-content': 'var(--color-base-black)',

    // Semantic
    '--color-semantic-error': 'var(--color-base-red-light)',

    // Text that uses legacy greys in the default theme
    '--color-label-checkbox-disabled': 'var(--color-ui-content-secondary)',
    '--color-label-checkbox-loading': 'var(--color-ui-content-secondary)',
    '--color-text-pdp-usp-description': 'var(--color-ui-content-secondary)',
    '--color-text-voucher-subtle': 'var(--color-ui-content-secondary)',

    // Surfaces that are pure white in the default theme
    '--color-bg-beige': 'var(--color-ui-bg-block)',
    '--color-bg-checkout-act-content': 'var(--color-ui-bg-surface)',
    '--color-bg-checkout-act-information': 'var(--color-ui-bg-surface)',
    '--color-bg-checkout-act-vouchers': 'var(--color-ui-bg-surface)',
    '--color-bg-checkout-info-boxes': 'var(--color-ui-bg-surface)',
    '--color-bg-highlight': 'var(--color-ui-bg-surface)',
    '--color-bg-pdp-flipclock': 'var(--color-ui-bg-surface)',
    '--color-bg-white': 'var(--color-ui-bg-surface)',

    '--color-border-thumbnail': 'var(--color-base-white-25)',
    '--color-text-focus-checkbox-label': 'var(--color-base-white-10)',

    // Scrollbar
    '--color-scrollbar-thumb': 'var(--color-base-beige-500)',
    '--color-scrollbar-track': 'var(--color-base-beige-800)',
};

const getLightColorSchemeCssVariables = (): CssVariables<string> => {
    const statics = new Map(Object.entries(defaultTheme.statics));
    const modeDependentKeys = new Set(Object.keys(darkColorCssVariables));
    const referencesModeDependentKey = (value: unknown) =>
        typeof value === 'string' && (value.match(/--[\w-]+/g) || []).some(key => modeDependentKeys.has(key));

    let hasNewKeys = true;
    while (hasNewKeys) {
        hasNewKeys = false;
        statics.forEach((value, key) => {
            if (!modeDependentKeys.has(key) && referencesModeDependentKey(value)) {
                modeDependentKeys.add(key);
                hasNewKeys = true;
            }
        });
    }

    return Object.fromEntries(
        Array.from(modeDependentKeys)
            .filter(key => statics.has(key))
            .map(key => [key, statics.get(key)])
    );
};

export const lightColorSchemeCssVariables = getLightColorSchemeCssVariables();
